// ✅ src/pages/Inventory/DieselReport.jsx
// FULL FILE - UPDATED WITH LOCALSTORAGE HELPER + COMMON HEADERS

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProjectPostOrderLayoutWrapper from "../../components/ProjectPostOrderLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSearch, FaEye } from "react-icons/fa";

const API_BASE = "http://nissi-india.com/api/dieselReport";

// ✅ normalize ANY backend response into array safely
const asArray = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  if (Array.isArray(payload.data)) return payload.data;
  if (payload.data && Array.isArray(payload.data.data)) return payload.data.data;

  if (payload.data && Array.isArray(payload.data.rows)) return payload.data.rows;
  if (payload.data && payload.data.data && Array.isArray(payload.data.data.rows)) {
    return payload.data.data.rows;
  }

  if (Array.isArray(payload.recordset)) return payload.recordset;
  if (payload.data && Array.isArray(payload.data.recordset)) return payload.data.recordset;

  if (Array.isArray(payload.rows)) return payload.rows;

  return [];
};

const toNum = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};

const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const toStr = (v) => String(v ?? "").trim();

// ✅ read ids from querystring + fallback to sessionStorage
const readReportContext = () => {
  try {
    const qs = new URLSearchParams(window.location.search);

    const q_pcProjId = qs.get("pcProjId");
    const q_pcBrId = qs.get("pcBrId");
    const q_selPcId = qs.get("selPcId");
    const q_viewType = qs.get("viewType");

    const s_pcProjId =
      sessionStorage.getItem("PCProjid") || sessionStorage.getItem("projId");
    const s_pcBrId =
      sessionStorage.getItem("PCBrid") || sessionStorage.getItem("branchId");
    const s_selPcId = sessionStorage.getItem("SelProjectCostingId");
    const s_viewType = sessionStorage.getItem("ViewType");

    const pcProjId = Number(q_pcProjId || s_pcProjId || 0) || 0;
    const pcBrId = Number(q_pcBrId || s_pcBrId || 0) || 0;
    const selPcId = Number(q_selPcId || s_selPcId || 0) || 0;
    const viewType = String(q_viewType || s_viewType || "Diesel");

    return { pcProjId, pcBrId, selPcId, viewType };
  } catch (err) {
    return { pcProjId: 0, pcBrId: 0, selPcId: 0, viewType: "Diesel" };
  }
};

const DieselReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ safe localStorage helper
  const getLS = (key, fallback = "") => {
    try {
      if (typeof window === "undefined") return fallback;
      const val = localStorage.getItem(key);
      return val !== null && val !== undefined ? val : fallback;
    } catch (err) {
      return fallback;
    }
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ parsed localStorage values
  const loginBranchId = useMemo(() => toInt(branchIdLS, 0), [branchIdLS]);
  const branchType = useMemo(() => toStr(branchTypeLS || "HO"), [branchTypeLS]);
  const loginUserId = useMemo(() => toStr(userIdLS), [userIdLS]);

  // ✅ report context from query/session
  const ctx = useMemo(() => readReportContext(), []);

  // ✅ final ids
  const branchId = useMemo(
    () => toInt(ctx.pcBrId || loginBranchId || 0, 0),
    [ctx.pcBrId, loginBranchId]
  );

  const projId = useMemo(() => toInt(ctx.pcProjId || 0, 0), [ctx.pcProjId]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": loginUserId,
    }),
    [finYearId, finYear, branchId, branchType, loginUserId]
  );

  // filters
  const [form, setForm] = useState({
    fromDate: "03/01/2026",
    toDate: "10/01/2026",
    storeId: String(branchId || ""),
    issueNo: "",
  });

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // dropdown + grid
  const [stores, setStores] = useState([]);
  const [rows, setRows] = useState([]);

  // loading
  const [loadingStores, setLoadingStores] = useState(false);
  const [loadingGrid, setLoadingGrid] = useState(false);

  // grand total
  const grandTotal = useMemo(() => {
    const sum = rows.reduce(
      (s, r) => s + toNum(r.fld_Total ?? r.total ?? r.Total ?? 0),
      0
    );
    return sum.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, [rows]);

  // ---- API ----
  const loadStores = async (brId = branchId) => {
    if (!brId) {
      setStores([]);
      return;
    }

    setLoadingStores(true);
    try {
      const res = await axios.get(`${API_BASE}/stores`, {
        params: { branchId: brId },
        headers: commonHeaders,
      });

      const list1 = asArray(res.data);
      const list2 = list1.length ? list1 : asArray(res.data?.data);
      const list3 = list2.length ? list2 : asArray(res.data?.data?.stores);

      const finalList = Array.isArray(list3) ? list3 : [];
      setStores(finalList);

      setF("storeId", String(brId));
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to load stores");
      setStores([]);
      setF("storeId", String(brId));
    } finally {
      setLoadingStores(false);
    }
  };

  const search = async (brId = branchId, prId = projId) => {
    if (!form.fromDate || !form.toDate) {
      toast.error("Select From Date and To Date");
      return;
    }

    if (!brId) {
      toast.error("BranchId missing");
      return;
    }

    if (!prId) {
      toast.warning("Project Id (pcProjId / PCProjid) not found in URL/sessionStorage");
    }

    setLoadingGrid(true);
    try {
      const res = await axios.get(`${API_BASE}/search`, {
        params: {
          branchId: brId,
          fromDate: form.fromDate,
          toDate: form.toDate,
          storeId: form.storeId || "%",
          issueNo: form.issueNo || "",
          projId: prId || "",
          contractId: prId || "",
          projectId: prId || "",
          // selPcId: ctx.selPcId,
          // viewType: ctx.viewType,
        },
        headers: commonHeaders,
      });

      const list1 = asArray(res.data);
      const list2 = list1.length ? list1 : asArray(res.data?.data);
      const list3 = list2.length ? list2 : asArray(res.data?.data?.rows);

      setRows(Array.isArray(list3) ? list3 : []);
      if (!list3 || list3.length === 0) toast.info("No Records Found!");
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Search failed");
      setRows([]);
    } finally {
      setLoadingGrid(false);
    }
  };

  const viewRow = (r) => {
    const issueId = r.issueId ?? r.fld_StoreIssueId ?? r.storeIssueId ?? r.id ?? 0;

    if (!issueId) {
      toast.info("No issue id found for this row");
      return;
    }

    // Example:
    // window.open(`/Inventory/StoreIssueRptView?id=${issueId}`, "_blank");

    toast.info(`View clicked (issueId: ${issueId})`);
  };

  useEffect(() => {
    if (branchId) {
      setForm((p) => ({
        ...p,
        storeId: String(branchId),
      }));
    }
  }, [branchId]);

  useEffect(() => {
    if (!projId) toast.info("Project not found in URL/session (pcProjId / PCProjid)");
    if (!branchId) toast.info("Branch not found in URL/session (pcBrId / PCBrid)");

    if (branchId) loadStores(branchId);
    if (branchId) search(branchId, projId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ProjectPostOrderLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">DIESEL REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Filters */}
          <div
            className={`p-6 rounded-xl border mb-6 ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left */}
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    From Date
                  </label>
                  <input
                    type="text"
                    value={form.fromDate}
                    onChange={(e) => setF("fromDate", e.target.value)}
                    placeholder="DD/MM/YYYY"
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Store
                  </label>
                  <select
                    value={form.storeId}
                    onChange={(e) => setF("storeId", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    {(stores || []).length === 0 ? (
                      <option value={String(branchId || "")}>
                        Branch {branchId || ""}
                      </option>
                    ) : (
                      <>
                        <option value="">Select</option>
                        {(stores || []).map((s, idx) => {
                          const id = String(
                            s.storeId ??
                              s.fld_storeid ??
                              s.branchId ??
                              s.fld_branchid ??
                              s.id ??
                              idx
                          );

                          const name =
                            s.storeName ??
                            s.fld_storename ??
                            s.branchName ??
                            s.fld_branchname ??
                            s.name ??
                            `Store ${id}`;

                          return (
                            <option key={id} value={id}>
                              {name}
                            </option>
                          );
                        })}
                      </>
                    )}
                  </select>
                  {loadingStores && <div className="text-xs mt-1 opacity-70">Loading stores…</div>}
                </div>
              </div>

              {/* Right */}
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    To Date
                  </label>
                  <input
                    type="text"
                    value={form.toDate}
                    onChange={(e) => setF("toDate", e.target.value)}
                    placeholder="DD/MM/YYYY"
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Issue No
                  </label>
                  <input
                    type="text"
                    value={form.issueNo}
                    onChange={(e) => setF("issueNo", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-center mt-6">
              <button
                onClick={() => search(branchId, projId)}
                disabled={loadingGrid}
                className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg transition-all duration-200 ${
                  loadingGrid ? "bg-gray-400 text-white cursor-not-allowed" : `${buttonClass} ${hoverClass} text-white`
                }`}
              >
                <FaSearch />
                {loadingGrid ? "Searching..." : "Search"}
              </button>
            </div>

            <div className={`mt-4 text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              <div>
                <b>Using:</b> projId={projId} | branchId={branchId} | selPcId={ctx.selPcId} | viewType={ctx.viewType}
              </div>
              <div>
                <b>Headers:</b> finYearId={finYearId} | finYear={finYear} | branchType={branchType} | userId={loginUserId}
              </div>
            </div>
          </div>

          {/* Grid */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Slno</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Store Issue no</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Store</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Branch</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Date</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Issue To</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Name</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Code</th>
                    <th className="px-4 py-4 text-center font-semibold">View</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loadingGrid ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-8 text-center">
                        Loading…
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr className={isDark ? "bg-gray-800" : "bg-gray-50"}>
                      <td
                        colSpan={9}
                        className={`text-center py-8 text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}
                      >
                        No Records Found!
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr
                        key={r.issueId ?? r.fld_StoreIssueId ?? r.storeIssueId ?? r.id ?? idx}
                        className={`${isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"} transition-colors`}
                      >
                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {r.slno ?? r.Slno ?? idx + 1}
                        </td>
                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {r.storeIssueNo ?? r.fld_storesissueno ?? r.issueNo ?? ""}
                        </td>
                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {r.store ?? r.fld_storename ?? r.storeName ?? ""}
                        </td>
                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {r.branch ?? r.fld_branchname ?? r.branchName ?? ""}
                        </td>
                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {r.date ?? r.fld_date ?? r.fld_IssueDate ?? ""}
                        </td>
                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {r.issueTo ?? r.fld_issueto ?? ""}
                        </td>
                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {r.name ?? r.fld_name ?? ""}
                        </td>
                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {r.code ?? r.fld_code ?? ""}
                        </td>
                        <td className={`px-4 py-3 text-center ${isDark ? "text-white" : "text-gray-900"}`}>
                          <button
                            onClick={() => viewRow(r)}
                            className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg text-xs font-semibold ${
                              isDark ? "bg-blue-700 hover:bg-blue-600 text-white" : "bg-blue-100 hover:bg-blue-200 text-blue-700"
                            }`}
                          >
                            <FaEye />
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Grand Total */}
          <div className="flex justify-end mt-4">
            <div className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
              Grand Total : {grandTotal}
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPostOrderLayoutWrapper>
  );
};

export default DieselReport;